import { useMemo } from 'react';
import type { GQLOrganizationStats } from '../graphql/organizations/useOrganization';
import { useDashboardStatsGQL } from '../graphql/dashboard/useDashboardStatsGQL';
import { useDashboardLeadsGQL } from '../graphql/dashboard/useDashboardLeadsGQL';
import { useOrgCallsGQL } from '../graphql/dashboard/useOrgCallsGQL';
import { useMessagesGQL } from '../graphql/dashboard/useMessagesGQL';
import { useOrgWebActivitiesGQL } from '../graphql/dashboard/useOrgWebActivitiesGQL';

interface DashboardLead {
  fullName: string;
  phoneNumber: string;
  email: string;
  dateTime: string;
  leadSource: string;
  conversionSource: string;
  intent: string;
  valuationRequested?: { address: string; requestedAt: string }[];
  claimedLead?: {
    name: string;
    phoneNumber: string;
    id?: string;
    email?: string;
    bio?: string;
    photo_url?: string;
  };
}

interface DashboardData {
  stats: {
    touches: number;
    calls: number;
    emails: number;
    webConversions: number;
    leads: {
      value: number;
      email: number;
      website: number;
      ppc: number;
    };
    opportunities: {
      value: number;
      percentage: number;
    };
    opportunityCallsTotal: number;
    liveTransfers: number;
    propertyViewsTotal: number;
    leadCapturesTotal: number;
    emailDeliveredTotal: number;
    emailOpensTotal: number;
    emailClicksTotal: number;
    orgStats: GQLOrganizationStats | null;
  };
  leads: DashboardLead[];
}

interface DashboardParams {
  dateRange: {
    from: Date | null;
    to: Date | null;
  };
  isLeadsModalOpen?: boolean;
  isCallsModalOpen?: boolean;
  isEmailsModalOpen?: boolean;
  isWebsiteModalOpen?: boolean;
  isEmailEngagementModalOpen?: boolean;
  emailEngagementEventType?: 'open' | 'click';
  webEventTypes?: string[];
  webDeviceType?: string;
  webIsGuest?: boolean;
  webSearch?: string;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  leadsPageSize?: number;
  leadsLeadSources?: string[];
  leadsSearch?: string;
}

export const useDashboard = ({
  dateRange,
  isLeadsModalOpen = false,
  isCallsModalOpen = false,
  isEmailsModalOpen = false,
  isWebsiteModalOpen = false,
  isEmailEngagementModalOpen = false,
  emailEngagementEventType,
  webEventTypes,
  webDeviceType,
  webIsGuest,
  webSearch,
  sortField = 'dateTime',
  sortDirection = 'desc',
  leadsPageSize = 25,
  leadsLeadSources,
  leadsSearch,
}: DashboardParams): {
  data: DashboardData;
  isLoading: boolean;
  hasMoreLeads: boolean;
  loadMoreLeads: () => void;
  refreshLeads: () => void;
  totalLeadsCount: number;
  leadsCurrentPage: number;
  leadsPageSize: number;
  goToLeadsPage: (page: number) => void;
  calls: ReturnType<typeof useOrgCallsGQL>;
  emails: ReturnType<typeof useMessagesGQL>;
  emailEngagement: ReturnType<typeof useMessagesGQL>;
  webActivities: ReturnType<typeof useOrgWebActivitiesGQL>;
} => {
  // GQL stats: leads by source, calls count, emails count, web conversions, opportunities, org stats
  const { data: statsData, loading: statsLoading } = useDashboardStatsGQL({ dateRange });

  // GQL leads list for the modal + always-on lead counts by source
  const {
    data: leadsData,
    isLoading: leadsLoading,
    countsLoading: leadCountsLoading,
    totalCount: totalLeadsCount,
    leadCounts,
    currentPage: leadsCurrentPage,
    goToPage: goToLeadsPage,
    refresh: refreshLeads,
  } = useDashboardLeadsGQL({
    dateRange,
    isModalOpen: isLeadsModalOpen,
    sortField,
    sortDirection,
    pageSize: leadsPageSize,
    leadSources: leadsLeadSources,
    search: leadsSearch,
  });

  // GQL calls — items only fetched when modal is open; counts come from statsData (org counts query)
  const callsResult = useOrgCallsGQL({
    dateRange,
    enabled: isCallsModalOpen,
    pageSize: 25,
  });

  // E-Alert delivered drilldown — items only fetched when modal is open
  const emailsResult = useMessagesGQL({
    dateRange,
    emailLogEventTypes: ['delivered'],
    enabled: isEmailsModalOpen,
    pageSize: 25,
  });

  // E-Alert opens/clicks drilldown — only fetched when sub-stat modal is open
  const emailEngagementResult = useMessagesGQL({
    dateRange,
    emailLogEventTypes: emailEngagementEventType ? [emailEngagementEventType] : undefined,
    enabled: isEmailEngagementModalOpen,
    pageSize: 25,
  });

  // GQL web activities — items only fetched when modal is open; count comes from statsData
  const webActivitiesResult = useOrgWebActivitiesGQL({
    dateRange,
    enabled: isWebsiteModalOpen,
    eventTypes: webEventTypes,
    deviceType: webDeviceType,
    isGuest: webIsGuest,
    search: webSearch,
  });

  // Use statsData for counts (single org query); modal hooks only provide items when open
  const callsMade = statsData?.callsTotal || 0;
  const liveTransfersCount = statsData?.orgStats?.totalLiveTransfers ?? 0;

  const dashboardData = useMemo(() => {
    const totalTouches =
      callsMade +
      liveTransfersCount +
      (statsData?.emails || 0) +
      (statsData?.webConversions || 0);

    return {
      stats: {
        touches: totalTouches,
        calls: callsMade,
        emails: statsData?.emails || 0,
        webConversions: statsData?.webConversions || 0,
        leads: {
          value: statsData?.leadCounts?.all ?? leadCounts.all,
          email: statsData?.leadCounts?.email ?? leadCounts.email,
          website: statsData?.leadCounts?.organic ?? leadCounts.organic,
          ppc: statsData?.leadCounts?.ppc ?? leadCounts.ppc,
        },
        opportunities: {
          value: statsData?.opportunityCallsTotal || 0,
          percentage: 0,
        },
        opportunityCallsTotal: statsData?.opportunityCallsTotal ?? 0,
        liveTransfers: liveTransfersCount,
        propertyViewsTotal: statsData?.propertyViewsTotal ?? 0,
        leadCapturesTotal: statsData?.leadCapturesTotal ?? 0,
        emailDeliveredTotal: statsData?.emailDeliveredTotal ?? 0,
        emailOpensTotal: statsData?.emailOpensTotal ?? 0,
        emailClicksTotal: statsData?.emailClicksTotal ?? 0,
        orgStats: statsData?.orgStats ?? null,
      },
      leads: leadsData || [],
    };
  }, [statsData, callsMade, liveTransfersCount, leadsData]);

  return {
    data: dashboardData,
    isLoading: statsLoading || leadsLoading || leadCountsLoading,
    hasMoreLeads: false,
    loadMoreLeads: () => {},
    refreshLeads,
    totalLeadsCount,
    leadsCurrentPage,
    leadsPageSize,
    goToLeadsPage,
    // Inject counts from statsData; items populated only when modal is open
    calls: {
      ...callsResult,
      total: callsResult.total || callsMade,
      conversationsTotal: statsData?.conversationsTotal || 0,
    },
    emails: {
      ...emailsResult,
      total: emailsResult.total || statsData?.emails || 0,
    },
    emailEngagement: emailEngagementResult,
    webActivities: {
      ...webActivitiesResult,
      total: webActivitiesResult.total || statsData?.webConversions || 0,
    },
  };
};
